import React, { useState, useEffect, useRef, useCallback } from 'react';
import { XMarkIcon, ChevronLeftIcon, ChevronRightIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

// No interface needed since images are now stored as URL strings

export default function Gallery() {
  const [galleryImages, setGalleryImages] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [visibleImages, setVisibleImages] = useState<Set<number>>(new Set());
  const galleryRef = useRef<HTMLDivElement>(null);
  // Fetch gallery images from /data/gallery/gallery.json
  useEffect(() => {
    setLoading(true);
    const fetchGalleryImages = async () => {
      try {
        console.log('Fetching gallery images from /data/gallery/gallery.json');
        const response = await fetch('/data/gallery/gallery.json');
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const data = await response.json();
        console.log('Gallery data loaded:', data);
        
        if (data.images && Array.isArray(data.images)) {
          setGalleryImages(data.images);
          setError(null);
        } else {
          throw new Error('Invalid data format: expected images array');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        console.error('Failed to fetch gallery images:', errorMessage);
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchGalleryImages();
  }, []);
  const navigateImage = useCallback((direction: 'prev' | 'next') => {
    if (selectedImageIndex === null) return;
    let newIndex;
    if (direction === 'prev') {
      newIndex = selectedImageIndex > 0 ? selectedImageIndex - 1 : galleryImages.length - 1;
    } else {
      newIndex = selectedImageIndex < galleryImages.length - 1 ? selectedImageIndex + 1 : 0;
    }
    setSelectedImageIndex(newIndex);
    setIsImageLoaded(false);
  }, [selectedImageIndex, galleryImages.length]);

  // Handle keyboard navigation
  useEffect(() => {
    if (selectedImageIndex === null) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSelectedImageIndex(null);
      } else if (e.key === 'ArrowLeft') {
        navigateImage('prev');
      } else if (e.key === 'ArrowRight') {
        navigateImage('next');
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedImageIndex, navigateImage]);
  // Intersection Observer for animations
  useEffect(() => {
    if (galleryImages.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const imageIdStr = entry.target.getAttribute('data-image-id');
            if (imageIdStr) {
              const imageId = parseInt(imageIdStr);
              setVisibleImages(prev => new Set(Array.from(prev).concat(imageId)));
            }
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' }
    );
    
    // Use setTimeout to ensure DOM elements are rendered
    setTimeout(() => {
      const imageElements = document.querySelectorAll('[data-image-id]');
      imageElements.forEach(el => observer.observe(el));
    }, 100);
    
    return () => observer.disconnect();
  }, [galleryImages]);

  const openLightbox = (index: number) => {
    setSelectedImageIndex(index);
    setIsImageLoaded(false);
    document.body.style.overflow = 'hidden';
  };
  const closeLightbox = () => {
    setSelectedImageIndex(null);
    document.body.style.overflow = 'auto';
  };

  return (
    <div className="bg-white py-24 sm:py-32 page-transition">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {/* Header */}
        <div className="mx-auto max-w-2xl lg:text-center mb-16">
          <h2 className="text-base font-semibold leading-7 text-primary-600">Memories</h2>
          <p className="mt-2 text-3xl font-bold tracking-tight text-neutral-900 sm:text-4xl">
            Photo Gallery
          </p>
          <p className="mt-6 text-lg leading-8 text-neutral-600">
            Take a visual journey through our homestay and discover the beauty, 
            culture, and experiences that await you in Bethanchok.
          </p>        </div>
        
        {/* Loading State */}
        {loading && (
          <div className="text-center py-16">
            <div className="mx-auto w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mb-4"></div>
            <p className="text-neutral-500 text-lg">Loading gallery...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-16">
            <div className="mx-auto w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <MagnifyingGlassIcon className="h-8 w-8 text-red-400" />
            </div>
            <p className="text-red-500 text-lg">Error loading gallery</p>
            <p className="text-red-400 text-sm mt-2">{error}</p>
          </div>
        )}

        {/* Masonry Photo Grid */}
        {!loading && !error && (
          <div 
            ref={galleryRef}
            className="columns-2 sm:columns-2 lg:columns-3 gap-4 sm:gap-6 space-y-4 sm:space-y-6"
          >          {galleryImages.map((imageObj, index) => (
            <div
              key={index}
              data-image-id={index}
              className={`group relative break-inside-avoid mb-4 sm:mb-6 rounded-2xl overflow-hidden cursor-pointer transform transition-all duration-500 hover:scale-[1.03] hover:shadow-2xl ${
                visibleImages.has(index) 
                  ? 'opacity-100 translate-y-0' 
                  : 'opacity-0 translate-y-8'
              }`}
              style={{ 
                transitionDelay: `${(index % 8) * 100}ms`,
                height: 'auto'
              }}
              onClick={() => openLightbox(index)}
            >              <img
                src={imageObj}
                alt={`Bethanchok homestay view ${index + 1}`}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                loading="lazy"
              />
              {/* Simple Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300" />            </div>
          ))}
          </div>
        )}

        {/* No images message */}
        {!loading && !error && galleryImages.length === 0 && (
          <div className="text-center py-16">
            <div className="mx-auto w-24 h-24 bg-neutral-100 rounded-full flex items-center justify-center mb-4">
              <MagnifyingGlassIcon className="h-8 w-8 text-neutral-400" />
            </div>
            <p className="text-neutral-500 text-lg">No images found.</p>
            <p className="text-neutral-400 text-sm mt-2">Please check back later.</p>
          </div>
        )}
      </div>
      {/* Enhanced Lightbox Modal */}
      {selectedImageIndex !== null && (
        <div 
          className="fixed inset-0 flex items-center justify-center bg-black/90 backdrop-blur-sm"
          style={{ zIndex: 9999 }}
          onClick={closeLightbox}
        >
          {/* Navigation */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigateImage('prev');
            }}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-2 sm:p-3 rounded-full bg-black/80 hover:bg-black/90 sm:bg-white/10 sm:hover:bg-white/20 text-white transition-all duration-300 sm:backdrop-blur-sm hover:scale-110"
            style={{ zIndex: 10001 }}
          >
            <ChevronLeftIcon className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigateImage('next');
            }}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-2 sm:p-3 rounded-full bg-black/80 hover:bg-black/90 sm:bg-white/10 sm:hover:bg-white/20 text-white transition-all duration-300 sm:backdrop-blur-sm hover:scale-110"
            style={{ zIndex: 10001 }}
          >
            <ChevronRightIcon className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
          {/* Close button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              closeLightbox();
            }}
            className="absolute top-6 right-6 p-2 sm:p-3 rounded-full bg-black/80 hover:bg-black/90 sm:bg-white/10 sm:hover:bg-white/20 text-white transition-all duration-300 sm:backdrop-blur-sm hover:scale-110"
            style={{ zIndex: 10001 }}
          >
            <XMarkIcon className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
          {/* Image counter */}
          <div 
            className="absolute top-6 left-6 px-2 py-1 sm:px-4 sm:py-2 rounded-full bg-black/80 sm:bg-white/10 sm:backdrop-blur-sm text-white text-xs sm:text-sm font-medium"
            style={{ zIndex: 10000 }}
          >
            {selectedImageIndex + 1} / {galleryImages.length}
          </div>
          {/* Main image container - updated for better centering and no cutoff */}
          <div 
            className="relative flex items-center justify-center max-w-5xl w-full h-full max-h-[90vh] mx-4"
            style={{ zIndex: 9998 }}
            onClick={(e) => e.stopPropagation()}
          >            <img
              src={galleryImages[selectedImageIndex]}
              alt={`Bethanchok homestay view ${selectedImageIndex + 1}`}
              className={`max-w-full max-h-[90vh] w-auto h-auto object-contain rounded-xl transition-all duration-500 ${
                isImageLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
              }`}
              onLoad={() => setIsImageLoaded(true)}
            />
            {/* Loading state */}
            {!isImageLoaded && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-white/20 border-t-white rounded-full animate-spin"></div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}